<template>
  <div
    ref="hotspotImageContainer"
    class="hotspot-component-container"
  >
    <img
      v-if="attachment"
      id="hotspot-image"
      ref="hotspotImage"
      :src="attachment.url"
      @click="setTarget"
      @load="setupLogic"
    >
    <div
      ref="hotspotArea"
      :class="['hotspotArea', {'show': showHotspot}]"
    />
    <div
      ref="hotspotTarget"
      :class="['hotspot-target', {'attempted': hotspotAttempted}]"
    >
      <icon :name="targetIcon" />
    </div>
  </div>
</template>

<script>
import Icon from '@components/singletons/icon';
// see setup logic for reason debounce is commented out
// import debounce from 'lodash.debounce';

export default {
  components: { Icon },
  props: {
    answered: {
      type: Boolean,
      default: false,
    },
    correct: {
      type: Boolean,
      default: false,
    },
    flashcard: {
      type: Object,
      default: () => ({}),
    },
    flashcardState: {
      type: Object,
      default: () => ({}),
    },
    rawAnswerData: {
      type: Object,
    },
  },
  emits: ['set-raw-answer-data'],
  data() {
    return {
      programmedWidth: null,
      displayingWidth: null,
      displayingHeight: null,
      hotspotAttempted: false,
      programmedHeight: null,
      previousDisplayingWidth: null,
      previousDisplayingHeight: null,
      positionTop: null,
      positionLeft: null,
    };
  },
  computed: {
    attachment() {
      if (this.hotspot) {
        return this.$store.getters['attachments/get'][this.hotspot.attachmentId];
      }
    },
    hotspot() {
      return this.flashcard?.hotspots[0];
    },
    showHotspot() {
      // we only want to show the hotspot after they've answered and if the answer is wrong
      if (this.flashcardState.flashcardId) {
        return !this.correct;
      } else {
        return this.answered && !this.correct;
      }
    },
    targetIcon() {
      if (this.flashcardState.flashcardId || this.answered) {
        return this.correct ? 'hotspot-correct' : 'hotspot-incorrect';
      } else {
        return 'hotspot-selected';
      }
    },
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
  },
  created() {
    this.$watch('hotspot', (newHotspot, oldHotspot) => {
      if (newHotspot && newHotspot !== oldHotspot) {
        this.$store.dispatch('attachments/fetch', { ids: [newHotspot.attachmentId]});
      }
    }, { immediate: true });
  },
  methods: {
    hasRefs(...refNames) {
      // Defensive check for refs in test environments
      return refNames.every(refName => this.$refs[refName]);
    },
    handleHotspotArea() {
      if (!this.hasRefs('hotspotImage', 'hotspotArea')) {
        return;
      }

      // width of image when content team defined hotspot box
      this.programmedWidth = this.hotspot.width;

      // get the last width of the image it was at, to know how much to scale
      this.previousDisplayingWidth = this.displayingWidth || this.$refs['hotspotImage'].getBoundingClientRect().width;
      // get the current width of the image
      this.displayingWidth = this.$refs['hotspotImage'].getBoundingClientRect().width;
      this.previousDisplayingHeight = this.displayingHeight || this.$refs['hotspotImage'].getBoundingClientRect().height;
      this.displayingHeight = this.$refs['hotspotImage'].getBoundingClientRect().height;

      // get the container width to be able to factor in the right-side padding

      // use above calculations to figure out the height of image when content team defined hotspot box
      this.programmedHeight = (this.displayingHeight * this.programmedWidth) / this.displayingWidth; // will need to user later in rawAnswerData

      // ratio: y1/programmed witdth = n/displaying width  -- comparing the ratio between y1 when it was defined, to what we have now
      const top = (this.hotspot.y1 * this.displayingHeight) / this.programmedHeight;
      // ratio: x2/programmedWidth = n/displayingWidth  -- this will give us what x2 is, scaled to our current display
      const newX2 = ((this.hotspot.x2 * this.displayingWidth) / this.programmedWidth);
      const bottom = this.displayingHeight - ((this.hotspot.y2 * this.displayingHeight) / this.programmedHeight);
      const left = ((this.hotspot.x1 * this.displayingWidth) / this.programmedWidth);

      // calculate the width of the newly scaled hotspot area, using the newX2 and the left parameter
      const width = (newX2 - left).toFixed(3);

      this.$refs['hotspotArea'].style.top = top + 'px';
      // we'll use this instead of defining the right bound. more reliable and straightforward
      this.$refs['hotspotArea'].style.width = width + 'px';
      this.$refs['hotspotArea'].style.bottom = bottom + 'px';
      this.$refs['hotspotArea'].style.left = left + 'px';
    },
    setTarget(evt) {
      if (this.answered) {
        return;
      }
      if (!this.hasRefs('hotspotTarget')) {
        return;
      }
      const targetHalfWidth = this.$refs['hotspotTarget'].getBoundingClientRect().width / 2;
      const targetHalfHeight = this.$refs['hotspotTarget'].getBoundingClientRect().height / 2;

      this.positionTop = evt.offsetY - targetHalfHeight;
      this.positionLeft = evt.offsetX - targetHalfWidth;

      this.$refs['hotspotTarget'].style.top = this.positionTop + 'px';
      this.$refs['hotspotTarget'].style.left = this.positionLeft + 'px';

      this.hotspotAttempted = true;

      this.checkTarget({offsetX: evt.offsetX, offsetY: evt.offsetY});
    },
    checkTarget(offsets) {
      this.calculateAndSendRawAnswerData(offsets);
    },
    recalculateTarget() {
      if (!this.positionTop || !this.positionLeft) {
        return;
      }
      if (!this.hasRefs('hotspotTarget')) {
        return;
      }

      // currentTargetX (aka positionLeft) / previousDisplayingWidth = x / displayingWidth
      const x = (this.positionLeft * this.displayingWidth) / this.previousDisplayingWidth;
      // currentTargetY (aka positionTop) / previousDisplayingHeight = y / displayingHeight
      const y = (this.positionTop * this.displayingHeight) / this.previousDisplayingHeight;

      this.positionLeft = x;
      this.positionTop = y;

      this.$refs['hotspotTarget'].style.top = y + 'px';
      this.$refs['hotspotTarget'].style.left = x + 'px';
    },
    setInitialTarget() {
      if (!this.hasRefs('hotspotTarget')) {
        return;
      }

      // rawAnswerX / programmedWidth = x / displayingHeight
      const x = (this.flashcardState.rawAnswerData.x * this.displayingWidth) / this.programmedWidth;
      const y = (this.flashcardState.rawAnswerData.y * this.displayingHeight) / this.programmedHeight;

      const targetHalfWidth = this.$refs['hotspotTarget'].getBoundingClientRect().width / 2;
      const targetHalfHeight = this.$refs['hotspotTarget'].getBoundingClientRect().height / 2;

      this.$refs['hotspotTarget'].style.top = (y - targetHalfHeight) + 'px';
      this.$refs['hotspotTarget'].style.left = (x - targetHalfWidth) + 'px';

      this.hotspotAttempted = true;
    },
    calculateAndSendRawAnswerData(offsets) {
      const x = (offsets.offsetX * this.programmedWidth) / this.displayingWidth;
      const y = (offsets.offsetY * this.programmedHeight) / this.displayingHeight;

      this.$emit('set-raw-answer-data', {x, y});
    },
    handleResize() {
      this.handleHotspotArea();
      this.recalculateTarget();
    },
    setupLogic() {
      // debouncing could be used if we think it is necessary for performance
      // currently it causes refs issues
      // window.addEventListener('resize', debounce(this.handleResize, 200))
      window.addEventListener('resize', this.handleResize);

      this.handleHotspotArea();
      if (this.flashcardState.flashcardId) {
        this.setInitialTarget();
      } else {
        // used to move the target initially - needed to correct bug that requires it be moved to appear
        if (this.hasRefs('hotspotTarget')) {
          this.$refs['hotspotTarget'].style.top = '0';
          this.$refs['hotspotTarget'].style.left = '0';
        }

        this.hotspotAttempted = false;
      }
    },
  },
};
</script>

<style lang='scss' scoped>
  .hotspot-component-container {
    width: 100%;
    position: relative;
    // border: 1px blue solid;
    .hotspotArea {
      background-color: rgba(254, 45, 45, 0.5);
      position: absolute;
      border-radius: 10px;
      pointer-events: none;
      opacity: 0;
      &.show {
        opacity: 1;
      }
    }
    .hotspot-target {
      position: absolute;
      opacity: 0;
      &.attempted {
        opacity: 1;
      }
    }
    span {
      border: 1px green solid;
    }
    img {
      max-width: 100%;
      // border: 1px red solid;
    }
  }
</style>
